// Retain a *single* staff introduction invitation across passwordless sign-in.
// Email sign-in often opens a new tab, so sessionStorage is not sufficient.
// Tokens stay out of query strings, HTTP referrers and analytics. Never use
// this client-side state to authorize access; the worker JWT and server decide.
const KEY = 'caredock_staff_introduction_continuation_v1';
const CONTINUATION_MS = 60 * 60 * 1000;
const TOKEN_RE = /^[A-Za-z0-9_-]{40,120}$/;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const safeWorkforceSlug = (value) => {
  const slug = String(value || '').trim().toLowerCase();
  return slug.length <= 80 && SLUG_RE.test(slug) ? slug : '';
};

export function clearPendingStaffInvitation(storage = window.localStorage) {
  try { storage.removeItem(KEY); } catch (_e) { /* storage may be disabled */ }
}

export function pendingStaffInvitation(storage = window.localStorage, now = Date.now()) {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!TOKEN_RE.test(data?.token || '')
        || !safeWorkforceSlug(data?.workspaceSlug)
        || !Number.isFinite(data?.savedAt)
        || now < data.savedAt
        || now - data.savedAt > CONTINUATION_MS) {
      clearPendingStaffInvitation(storage);
      return null;
    }
    return { token: data.token, workspaceSlug: data.workspaceSlug };
  } catch (_e) {
    clearPendingStaffInvitation(storage);
    return null;
  }
}

export function captureStaffInvitation({
  hash = window.location.hash,
  search = window.location.search,
  storage = window.localStorage,
  now = Date.now(),
} = {}) {
  const parameters = new URLSearchParams(String(hash || '').replace(/^#/, ''));
  const token = parameters.get('invite');
  if (!token) return pendingStaffInvitation(storage, now);
  // A recipient tenant identifier is public metadata, not authorization.
  const query = new URLSearchParams(search || '');
  let storedWorkspaceSlug = '';
  try { storedWorkspaceSlug = storage.getItem('workerWorkspaceSlug') || ''; } catch (_e) { /* blocked storage */ }
  const workspaceSlug = safeWorkforceSlug(query.get('workspace'))
    || safeWorkforceSlug(storedWorkspaceSlug);
  if (!TOKEN_RE.test(token) || !workspaceSlug) {
    clearPendingStaffInvitation(storage);
    return null;
  }
  try {
    storage.setItem(KEY, JSON.stringify({ token, workspaceSlug, savedAt: now }));
  } catch (_e) {
    return null;
  }
  return { token, workspaceSlug };
}

export function staffInvitationSignInPath(pending) {
  const slug = safeWorkforceSlug(pending?.workspaceSlug);
  return slug ? `/workforce/${encodeURIComponent(slug)}?sign_in=1` : '/access';
}

export function hasMatchingPendingStaffInvitation(workspaceSlug, storage = window.localStorage) {
  const pending = pendingStaffInvitation(storage);
  return Boolean(pending && pending.workspaceSlug === safeWorkforceSlug(workspaceSlug));
}
